import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Badge, Breadcrumbs, Card, Empty, ErrorNotice, Loading, Modal, Notice, fmtDate, useToast,
} from '../components/ui.jsx';

const TABS = [
  { key: 'matching', label: 'Matching settings' },
  { key: 'bands', label: 'Premium bands' },
  { key: 'users', label: 'Users' },
  { key: 'audit', label: 'Audit trail' },
];

export default function AdminPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState('matching');

  if (!can('admin')) {
    return (
      <div className="page">
        <Breadcrumbs items={[{ label: 'Administration' }]} />
        <Notice tone="warn" title="Administrator access required">
          This section is limited to users with the admin role.
        </Notice>
      </div>
    );
  }

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Administration' }]} />
      <div className="page-head">
        <div>
          <h1>Administration</h1>
          <div className="page-sub">
            Matching thresholds, the rating slabs the band check uses, user access, and the audit trail.
          </div>
        </div>
      </div>

      <div className="btn-row" style={{ marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn ${tab === t.key ? 'btn-primary' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'matching' && <MatchingSettings />}
      {tab === 'bands' && <PremiumBands />}
      {tab === 'users' && <Users />}
      {tab === 'audit' && <AuditTrail />}
    </div>
  );
}

/* -------------------------- matching settings ------------------------- */

function MatchingSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [weights, setWeights] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [error, setError] = useState(null);

  const query = useQuery({ queryKey: ['matchConfig'], queryFn: () => api.matchConfig().then((r) => r.data) });
  const active = (query.data || []).find((c) => c.isActive) || (query.data || [])[0];

  useEffect(() => {
    if (active && !weights) { setWeights(active.weights); setThresholds(active.thresholds); }
  }, [active, weights]);

  const mutation = useMutation({
    mutationFn: () => api.updateMatchConfig(active.id, { weights, thresholds }),
    onSuccess: () => {
      toast.success('Matching profile updated. It applies to the next match run.');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['matchConfig'] });
    },
    onError: setError,
  });

  if (query.isLoading || !weights) return <Card><Loading rows={6} /></Card>;
  if (query.error) return <Card><ErrorNotice error={query.error} /></Card>;

  const sum = Object.values(weights).reduce((a, b) => a + Number(b), 0);

  return (
    <div className="cols cols-2">
      <Card
        title="Attribute weights"
        subtitle={`Profile "${active.name}". Weights must total 1.00.`}
        actions={<Badge kind={Math.abs(sum - 1) < 0.001 ? 'approved' : 'fail'}>{sum.toFixed(3)}</Badge>}
      >
        <ErrorNotice error={error} />
        {Object.entries(weights).map(([key, value]) => (
          <div className="field" key={key}>
            <label htmlFor={`w-${key}`}>{key} — {(Number(value) * 100).toFixed(0)}%</label>
            <input
              id={`w-${key}`} type="range" min="0" max="0.6" step="0.01" value={value}
              onChange={(e) => setWeights({ ...weights, [key]: Number(e.target.value) })}
            />
          </div>
        ))}
        <Notice tone="info" title="How weights are applied">
          An attribute that cannot be compared on a given pair - a blank variant, a missing cc - is excluded
          and its weight is spread across the attributes that did compare. Records with sparse data are
          therefore not penalised, but their scores rest on fewer signals.
        </Notice>
      </Card>

      <Card title="Thresholds" subtitle="Where the engine draws the line between auto-approval and human review">
        <div className="field">
          <label htmlFor="t-high">High confidence from</label>
          <input id="t-high" type="number" min="1" max="100" step="0.5" value={thresholds.high}
            onChange={(e) => setThresholds({ ...thresholds, high: Number(e.target.value) })} />
        </div>
        <div className="field">
          <label htmlFor="t-medium">Medium confidence from</label>
          <input id="t-medium" type="number" min="1" max="100" step="0.5" value={thresholds.medium}
            onChange={(e) => setThresholds({ ...thresholds, medium: Number(e.target.value) })} />
          <div className="field-hint">Must be lower than the high threshold.</div>
        </div>
        <div className="field">
          <label htmlFor="t-auto">Auto-approve from</label>
          <input id="t-auto" type="number" min="1" max="100" step="0.5" value={thresholds.autoApprove}
            onChange={(e) => setThresholds({ ...thresholds, autoApprove: Number(e.target.value) })} />
          <div className="field-hint">
            A score above this is still only auto-approved if the band check passes and the top candidate is
            at least 2 points clear of the runner-up.
          </div>
        </div>
        <div className="field">
          <label htmlFor="t-min">Minimum score to keep a candidate</label>
          <input id="t-min" type="number" min="0" max="100" step="1" value={thresholds.minCandidate}
            onChange={(e) => setThresholds({ ...thresholds, minCandidate: Number(e.target.value) })} />
        </div>
        <div className="field">
          <label htmlFor="t-limit">Candidates retained per record</label>
          <input id="t-limit" type="number" min="1" max="20" value={thresholds.candidateLimit}
            onChange={(e) => setThresholds({ ...thresholds, candidateLimit: Number(e.target.value) })} />
        </div>

        <div className="btn-row">
          <button
            className="btn btn-primary"
            disabled={mutation.isPending || Math.abs(sum - 1) > 0.001}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Save profile'}
          </button>
          <button className="btn" onClick={() => { setWeights(active.weights); setThresholds(active.thresholds); }}>
            Reset
          </button>
        </div>
        <div className="prov" style={{ marginTop: 8 }}>Last updated {fmtDate(active.updatedAt)}</div>
      </Card>
    </div>
  );
}

/* ---------------------------- premium bands --------------------------- */

function PremiumBands() {
  const query = useQuery({ queryKey: ['premiumBands'], queryFn: () => api.premiumBands().then((r) => r.data) });

  return (
    <Card
      title="Premium band slabs"
      subtitle="The band check compares the insurer record and the matched GIIB vehicle against these slabs."
      bodyClass="tight"
    >
      {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={5} /></div>
        : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
          : (query.data || []).length === 0 ? <Empty title="No bands configured">Run the reference seed.</Empty>
            : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Class</th><th>Band</th><th>Label</th>
                      <th className="num">cc from</th><th className="num">cc to</th>
                      <th className="num">kW from</th><th className="num">kW to</th>
                      <th className="num">Base premium</th><th>Effective from</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(query.data || []).map((b) => (
                      <tr key={b.id}>
                        <td>{b.class_code || '-'}</td>
                        <td className="mono">{b.band_code}</td>
                        <td>{b.label}</td>
                        <td className="num">{b.cc_from ?? '-'}</td>
                        <td className="num">{b.cc_to ?? '-'}</td>
                        <td className="num">{b.kw_from ?? '-'}</td>
                        <td className="num">{b.kw_to ?? '-'}</td>
                        <td className="num">{b.base_premium ?? '-'}</td>
                        <td>{b.effective_from ? String(b.effective_from).slice(0, 10) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      <div style={{ padding: 16 }}>
        <Notice tone="warn" title="Keep these in step with the tariff">
          Slabs and base premiums are seeded from the current motor tariff and must be re-confirmed whenever
          IRDAI revises it. Editing them changes which links pass the band check on the next match run;
          existing approved links are not revisited automatically.
        </Notice>
      </div>
    </Card>
  );
}

/* -------------------------------- users ------------------------------- */

function Users() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const query = useQuery({ queryKey: ['users'], queryFn: () => api.listUsers().then((r) => r.data) });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }) => api.updateUser(id, { isActive }),
    onSuccess: () => { toast.success('User updated'); queryClient.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <Card
        title="Users"
        subtitle="Viewers can read everything. Reviewers can import and decide mappings. Admins can also change settings and users."
        actions={<button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)}>Add user</button>}
        bodyClass="tight"
      >
        {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={4} /></div>
          : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
            : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last sign-in</th><th /></tr>
                  </thead>
                  <tbody>
                    {(query.data || []).map((u) => (
                      <tr key={u.id}>
                        <td className="cell-main">{u.name}</td>
                        <td>{u.email}</td>
                        <td><Badge kind="neutral">{u.role}</Badge></td>
                        <td><Badge kind={u.isActive ? 'approved' : 'superseded'}>{u.isActive ? 'active' : 'disabled'}</Badge></td>
                        <td>{fmtDate(u.lastLoginAt)}</td>
                        <td>
                          <button
                            className="btn btn-sm"
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate({ id: u.id, isActive: !u.isActive })}
                          >
                            {u.isActive ? 'Disable' : 'Enable'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Card>
      {addOpen && <AddUserModal onClose={() => setAddOpen(false)} />}
    </>
  );
}

function AddUserModal({ onClose }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ fullName: '', email: '', password: '', role: 'viewer' });
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const mutation = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: () => {
      toast.success('User created');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: setError,
  });

  const valid = form.fullName.trim().length > 1 && /.+@.+\..+/.test(form.email) && form.password.length >= 10;

  return (
    <Modal
      title="Add a user"
      onClose={onClose}
      footer={(
        <>
          <button className="btn btn-primary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Creating...' : 'Create user'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <ErrorNotice error={error} />
      <div className="grid-2">
        <div className="field">
          <label htmlFor="u-name">Full name</label>
          <input id="u-name" value={form.fullName} onChange={set('fullName')} />
        </div>
        <div className="field">
          <label htmlFor="u-email">Email</label>
          <input id="u-email" type="email" value={form.email} onChange={set('email')} />
        </div>
        <div className="field">
          <label htmlFor="u-pass">Temporary password</label>
          <input id="u-pass" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" />
          <div className="field-hint">At least 10 characters. The user should change it after signing in.</div>
        </div>
        <div className="field">
          <label htmlFor="u-role">Role</label>
          <select id="u-role" value={form.role} onChange={set('role')}>
            <option value="viewer">Viewer</option>
            <option value="reviewer">Reviewer</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}

/* ----------------------------- audit trail ---------------------------- */

function AuditTrail() {
  const [entityType, setEntityType] = useState('');
  const query = useQuery({
    queryKey: ['audit', entityType],
    queryFn: () => api.audit({ entityType: entityType || undefined, limit: 100 }).then((r) => r.data),
  });

  return (
    <Card title="Audit trail" subtitle="Append-only. Every decision, import and settings change is recorded." bodyClass="tight">
      <div className="filters">
        <div className="f">
          <label htmlFor="au-type">Entity</label>
          <select id="au-type" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            <option value="">All</option>
            <option value="mapping">Mappings</option>
            <option value="giib_vehicle">GIIB vehicles</option>
            <option value="insurer">Insurers</option>
            <option value="import_batch">Imports</option>
            <option value="user">Users</option>
            <option value="match_config">Matching settings</option>
          </select>
        </div>
      </div>

      {query.isLoading ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
        : query.error ? <div style={{ padding: 16 }}><ErrorNotice error={query.error} /></div>
          : (query.data || []).length === 0 ? <Empty title="No audit entries" />
            : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>When</th><th>Who</th><th>Action</th><th>Entity</th><th>Details</th></tr>
                  </thead>
                  <tbody>
                    {(query.data || []).map((a) => (
                      <tr key={a.id}>
                        <td>{fmtDate(a.created_at)}</td>
                        <td>
                          {a.actor_email || <span className="prov">system</span>}
                          {a.ip_address && <div className="prov">{a.ip_address}</div>}
                        </td>
                        <td className="mono">{a.action}</td>
                        <td>
                          {a.entity_type}
                          {a.entity_id && <div className="prov">#{a.entity_id}</div>}
                        </td>
                        <td>
                          <details>
                            <summary className="prov">before / after</summary>
                            <pre className="mono" style={{ whiteSpace: 'pre-wrap', maxWidth: 460, margin: '6px 0 0' }}>
{JSON.stringify({ before: a.before_state, after: a.after_state }, null, 2)}
                            </pre>
                          </details>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
    </Card>
  );
}
